import { useState, useRef } from 'react';
import type { ReactNode } from 'react';
import Media from 'react-media';
import { flattenDeep } from 'lodash';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import {
  Chart,
  Axis,
  ChartTitle,
  Wrapper,
  Title,
  TitleTooltip,
} from './styled';
import { useTheme, Text, Box } from '@simplywallst/ui-core';
import Column from './components/Column';
import { slugify } from '@/utilities/slugify';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';
import Tooltip from '@/components/Tooltip';

type Value = number | null;

interface ComputedColumn {
  title: string;
  value: Value;
  formattedValue: string;
  width: number;
  x?: number;
  y: number;
  color: string;
  titleColor?: string;
  valueColor?: string;
  tooltip: ReactNode;
  height: number;
  showTopBar: boolean;
}

export interface EntityProps {
  showTopBar?: boolean;
  renderHeight?: number;
  renderDirection?: 'up' | 'down';
  negativeValueColor?: string;
}

interface ChartProps {
  title: string;
  zero: number;
  columns: ComputedColumn[];
}

export interface Props {
  canStack?: boolean;
  charts: string[];
  colors: string[];
  entities: Record<string, Record<string, number>>;
  entitiesFormatted: Record<string, Record<string, string>>;
  entitiesTooltips: Record<string, Record<string, ReactNode>>;
  typography?: keyof ReturnType<typeof useTheme>['typography'];
  hideTitle?: boolean;
  height: number;
  keys: string[];
  labels?: string[];
  titleColors?: string[];
  valueColors?: string[];
  entitiesProps: Record<string, Record<string, EntityProps>>;
  allowScrolling?: boolean;
}

const defaultProps = {
  canStack: true,
  titleColors: [],
  tooltips: [],
};

const WaterfallChart = ({
  canStack,
  charts,
  colors,
  entities,
  entitiesFormatted,
  entitiesTooltips,
  typography,
  height,
  hideTitle,
  keys,
  labels,
  titleColors,
  valueColors,
  entitiesProps,
  allowScrolling = false,
}: Props) => {
  const width = 100;
  const topOffset = 30;
  const theme = useTheme();
  const [stack, setStack] = useState(false);
  const [offset, setOffset] = useState(topOffset);
  const [chartWidth, setChartWidth] = useState('auto');

  const values: number[] = flattenDeep(
    charts.map((chart) =>
      keys.map((key) => {
        const renderHeight = (entitiesProps[chart] || {})[key]?.renderHeight;
        return renderHeight && renderHeight > 0
          ? renderHeight
          : entities[chart][key];
      })
    )
  ).filter((n) => n !== null) as number[];

  const max = Math.max(0, ...values);
  const min = Math.min(0, ...values);
  const xScale = scaleBand({
    domain: keys,
    range: [0, width],
  });

  const yScale = scaleLinear({
    domain: [min, max],
    range: [height, offset],
  });

  const color = scaleOrdinal({
    domain: keys,
    range: colors,
  });

  const titleColor = scaleOrdinal({
    domain: keys,
    range: titleColors,
  });

  const valueColor = scaleOrdinal({
    domain: keys,
    range: valueColors,
  });

  const computedCharts: ChartProps[] = charts.map((chart): ChartProps => {
    //  If canStack doesn't === false
    if (canStack && stack) {
      const values: number[] = flattenDeep(
        keys.map((key) => entities[chart][key])
      ).filter((n) => n !== null) as number[];
      const max = Math.max(0, ...values);
      const min = Math.min(0, ...values);
      //  Reset domain min/max
      yScale.domain([min, max]);
    }
    //  Get zero after yScale.domain has been potentially altered due to stacking
    const zero = yScale(0) as number;
    //
    return {
      zero,
      title: chart,
      columns: keys.map((key, index): ComputedColumn => {
        const value = entities[chart][key];
        const formattedValue = entitiesFormatted[chart][key];
        const height = yScale(value);
        const renderHeight = (entitiesProps[chart] || {})[key]?.renderHeight;
        const renderDirection =
          (entitiesProps[chart] || {})[key]?.renderDirection || 'down';
        const negativeValueColor =
          (entitiesProps[chart] || {})[key]?.negativeValueColor ||
          theme.color.bad;
        let yCoord = value >= 0 ? height : zero;
        //when provided use renderHeight instead of column height
        if (renderHeight !== undefined) {
          yCoord =
            renderDirection === 'up'
              ? yScale(renderHeight) + height - zero
              : yScale(renderHeight);
        }
        const showTopBar =
          (entitiesProps[chart] || {})[key]?.showTopBar || false;

        return {
          title: typeof labels !== 'undefined' ? labels[index] : key,
          value,
          formattedValue,
          width: width / keys.length,
          x: xScale(key),
          y: yCoord,
          color: value !== null && value <= 0 ? negativeValueColor : color(key),
          tooltip: entitiesTooltips[chart][key],
          titleColor:
            titleColors && titleColors.length > 0 ? titleColor(key) : undefined,
          valueColor:
            valueColors && valueColors.length > 0 ? valueColor(key) : undefined,
          height: Math.abs(height - zero),
          showTopBar,
        };
      }),
    };
  });

  const labelOffset = 30;
  const labelTopMargin = 16;
  const colRef = useRef<SVGSVGElement>(null);
  const getTerm = useGlossaryTerms();
  const chartHeight = 355;
  return (
    <Box overflow="auto">
      <Box width={chartWidth} height={chartHeight}>
        <Media
          query={`(max-width: ${theme.smMin})`}
          onChange={(matches) =>
            matches && allowScrolling
              ? setChartWidth('580px')
              : setChartWidth('auto')
          }
        />
        {canStack && (
          <Media
            query={`(min-width: ${theme.mdMin})`}
            onChange={(matches) => (matches ? setStack(false) : setStack(true))}
          />
        )}
        {computedCharts.map((chart, i) => {
          const offset = RUNTIME_ENV === 'server' ? 26 : 0;
          return (
            <Wrapper
              key={`column-group-${i}-${chart.title}`}
              col={canStack ? 12 : 12 / charts.length}
              colMd={12 / charts.length}
              canStack={canStack}
            >
              <div>
                <Axis>
                  <svg width="100%" height={height} shapeRendering="crispEdges">
                    <rect
                      x="0"
                      y={chart.zero - 1 + offset}
                      width="100%"
                      height="1"
                      fill={theme.color.white}
                    />
                  </svg>
                </Axis>
                <Chart>
                  <svg
                    width="100%"
                    height={height + offset + labelOffset + labelTopMargin}
                    shapeRendering="crispEdges"
                    data-cy-id={`waterfall-chart-${slugify(chart.title)}`}
                  >
                    {chart.columns.map(
                      ({
                        color,
                        height: columnHeight,
                        titleColor,
                        valueColor,
                        title,
                        tooltip,
                        value,
                        formattedValue,
                        width: columnWidth,
                        x,
                        y,
                        showTopBar,
                      }) => {
                        const visibleColumnHeight =
                          columnHeight < 1 ? 1 : columnHeight;
                        const term = getTerm(title.toLowerCase());
                        return (
                          <g key={`column-${i}-${chart.title}-${title}`}>
                            <Column
                              ref={colRef}
                              x={`${((x || 0) / width) * 100}%`}
                              y={y}
                              tooltip={tooltip}
                              columnWidth={`${columnWidth}%`}
                              columnHeight={visibleColumnHeight}
                              color={color}
                              title={title}
                              valueColor={valueColor}
                              value={value}
                              formattedValue={formattedValue}
                              zero={chart.zero}
                              typography={typography}
                              chartHeight={height}
                              labelOffset={labelOffset}
                              onUpdate={(labelHeight) => {
                                const area = height + (chart.zero - height);
                                let currentOffset = 0;
                                if (y === chart.zero) {
                                  currentOffset = area - labelHeight;
                                } else {
                                  currentOffset =
                                    area - labelHeight - columnHeight;
                                }
                                const offsetDiff = Math.abs(
                                  offset - Math.round(currentOffset)
                                );
                                if (currentOffset < 0 && offsetDiff > 2) {
                                  setOffset(Math.abs(labelHeight));
                                }
                              }}
                              showTopBar={showTopBar}
                            />
                            <rect
                              x="0"
                              y={height - 1}
                              width="100%"
                              height="1"
                              fill={theme.color.lightGrayRGBA(0.1)}
                            />
                            <foreignObject
                              y={height + labelTopMargin}
                              x={`${((x || 0) / width) * 100}%`}
                              width={`${columnWidth}%`}
                              height={70}
                            >
                              {typeof term === 'string' && term !== '' ? (
                                <Tooltip
                                  openDelay={300}
                                  light
                                  title={<Box maxWidth={200}>{term}</Box>}
                                  placement="top"
                                >
                                  <Box paddingRight="x0_5">
                                    <Text typography="tinyStrong">
                                      <TitleTooltip color={titleColor}>
                                        {title}
                                      </TitleTooltip>
                                    </Text>
                                  </Box>
                                </Tooltip>
                              ) : (
                                <Box paddingRight="x0_5">
                                  <Text typography="tinyStrong">
                                    <Title color={titleColor}>{title}</Title>
                                  </Text>
                                </Box>
                              )}
                            </foreignObject>
                          </g>
                        );
                      }
                    )}
                  </svg>
                </Chart>
                {!hideTitle && <ChartTitle>{chart.title}</ChartTitle>}
              </div>
            </Wrapper>
          );
        })}
      </Box>
    </Box>
  );
};

WaterfallChart.defaultProps = defaultProps;

export default WaterfallChart;
